import { z } from 'zod';
import { AccessScopeKindSchema, ScopeRefSchema } from './access';
import {
  OrgStatusSchema,
  PositionKindSchema,
  PositionSummarySchema,
  SiteFormatSchema,
  SiteKindSchema,
  SiteStatusSchema,
} from './org';
import { PositionHistoryItemSchema } from './people';

/**
 * Phase 1 M4: positions, grants, covers and organisation admin (spec §2 rule 1, §5 screens
 * 2–4, §8; D10, D19, D22, D24). Every request here is a mutation the API audits.
 */

const IsoDate = z.iso.date();

/** A scope as a request names it: company, or a site/region/division by id. */
export const ScopeInputSchema = z.object({
  kind: AccessScopeKindSchema,
  /** Site/region/division id; null for company. The API checks it exists. */
  id: z.string().min(1).max(36).nullable(),
});
export type ScopeInput = z.infer<typeof ScopeInputSchema>;

// Positions -------------------------------------------------------------------

export const AssignPositionRequestSchema = z.object({
  positionCode: z.string().trim().min(1).max(64),
  from: IsoDate,
  isActing: z.boolean().optional(),
  /** End the seat's current holder on the day before `from` (otherwise 409 `seat_occupied`). */
  endCurrentHolder: z.boolean().optional(),
  /** Promotion / transfer: also end this person's other current seats (spec §2 rule 1). */
  replaceCurrent: z.boolean().optional(),
});
export type AssignPositionRequest = z.infer<typeof AssignPositionRequestSchema>;

export const EndedAssignmentSchema = z.object({
  assignmentId: z.uuid(),
  personId: z.uuid(),
  personName: z.string(),
  positionCode: z.string(),
  endedOn: IsoDate,
});

export const AssignPositionResponseSchema = z.object({
  assignment: PositionHistoryItemSchema,
  ended: z.array(EndedAssignmentSchema),
  /** Sessions of the people whose seats ended (they sign in again with their new access). */
  sessionsRevoked: z.number().int().nonnegative(),
});
export type AssignPositionResponse = z.infer<typeof AssignPositionResponseSchema>;

/** 409 when the seat already has a holder and `endCurrentHolder` was not set. */
export const SeatOccupiedErrorSchema = z.object({
  error: z.literal('seat_occupied'),
  message: z.string(),
  holder: z.object({
    personId: z.uuid(),
    name: z.string(),
    since: IsoDate,
    isActing: z.boolean(),
  }),
});
export type SeatOccupiedError = z.infer<typeof SeatOccupiedErrorSchema>;

export const EndAssignmentRequestSchema = z.object({ to: IsoDate });
export type EndAssignmentRequest = z.infer<typeof EndAssignmentRequestSchema>;

export const EndAssignmentResponseSchema = z.object({
  assignment: PositionHistoryItemSchema,
  sessionsRevoked: z.number().int().nonnegative(),
});

/** One seat on the Positions screen (spec §5 screen 4). */
export const PositionListItemSchema = PositionSummarySchema.extend({
  scope: ScopeRefSchema,
  /** The region the seat sits in (its own region, or its site's); null for divisional seats. */
  regionCode: z.string().nullable(),
  regionName: z.string().nullable(),
  grantsRole: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).nullable(),
  reportsTo: z.object({ code: z.string(), title: z.string() }).nullable(),
  status: z.enum(['active', 'closed']),
  /** Who is covering the seat today or soon (D24). */
  covers: z.array(
    z.object({
      id: z.uuid(),
      personId: z.uuid(),
      name: z.string(),
      from: IsoDate,
      to: IsoDate,
    }),
  ),
});
export type PositionListItem = z.infer<typeof PositionListItemSchema>;

export const PositionListResponseSchema = z.object({
  positions: z.array(PositionListItemSchema),
});
export type PositionListResponse = z.infer<typeof PositionListResponseSchema>;

// Grants ----------------------------------------------------------------------

export const GrantSchema = z.object({
  id: z.uuid(),
  role: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
  scope: ScopeRefSchema,
  from: IsoDate,
  to: IsoDate.nullable(),
  reason: z.string().nullable(),
  grantedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  revokedAt: z.iso.datetime().nullable(),
  /** Active today: not revoked, started, not yet ended. */
  active: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type Grant = z.infer<typeof GrantSchema>;

export const GrantsResponseSchema = z.object({ grants: z.array(GrantSchema) });
export type GrantsResponse = z.infer<typeof GrantsResponseSchema>;

export const GrantRequestSchema = z
  .object({
    roleId: z.uuid(),
    scope: ScopeInputSchema,
    from: IsoDate,
    to: IsoDate.nullable().optional(),
    reason: z.string().trim().min(2).max(500),
  })
  .refine((g) => !g.to || g.to >= g.from, {
    message: 'End date is before the start',
    path: ['to'],
  });
export type GrantRequest = z.infer<typeof GrantRequestSchema>;

/** 403 when the role carries permissions the caller does not hold at that scope. */
export const EscalationErrorSchema = z.object({
  error: z.literal('escalation'),
  message: z.string(),
  permissions: z.array(z.string()),
});
export type EscalationError = z.infer<typeof EscalationErrorSchema>;

// Covers (D24) ----------------------------------------------------------------

export const CoverSchema = z.object({
  id: z.uuid(),
  position: z.object({
    id: z.uuid(),
    code: z.string(),
    title: z.string(),
    kind: PositionKindSchema,
  }),
  scope: ScopeRefSchema,
  /** The substantive holder of the covered seat, if any. */
  holderName: z.string().nullable(),
  from: IsoDate,
  to: IsoDate,
  reason: z.string().nullable(),
  createdBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  active: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type Cover = z.infer<typeof CoverSchema>;

export const CoversResponseSchema = z.object({ covers: z.array(CoverSchema) });
export type CoversResponse = z.infer<typeof CoversResponseSchema>;

export const CoverRequestSchema = z
  .object({
    positionId: z.uuid(),
    from: IsoDate,
    to: IsoDate,
    reason: z.string().trim().max(500).optional(),
  })
  .refine((c) => c.to >= c.from, { message: 'End date is before the start', path: ['to'] });
export type CoverRequest = z.infer<typeof CoverRequestSchema>;

// Organisation admin (D19) ----------------------------------------------------

const Code = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9_]{0,31}$/, 'Codes are letters, digits and underscores');
const Name = z.string().trim().min(1).max(100);

export const DivisionPatchSchema = z.object({
  name: Name.optional(),
  status: OrgStatusSchema.optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type DivisionPatch = z.infer<typeof DivisionPatchSchema>;

export const DivisionCreateSchema = z.object({ code: Code, name: Name });
export type DivisionCreate = z.infer<typeof DivisionCreateSchema>;

export const RegionPatchSchema = z.object({
  name: Name.optional(),
  divisionCode: Code.nullable().optional(),
  status: OrgStatusSchema.optional(),
});
export type RegionPatch = z.infer<typeof RegionPatchSchema>;

export const RegionCreateSchema = z.object({
  code: Code,
  name: Name,
  divisionCode: Code.nullable().optional(),
});
export type RegionCreate = z.infer<typeof RegionCreateSchema>;

/** IPv4 CIDR, e.g. 81.2.3.0/24. */
export const CIDR_PATTERN =
  /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}\/(3[0-2]|[12]?\d)$/;

export const SitePatchSchema = z
  .object({
    name: Name.optional(),
    kind: SiteKindSchema.optional(),
    format: SiteFormatSchema.optional(),
    regionCode: Code.optional(),
    divisionCode: Code.optional(),
    address: z.string().trim().max(255).nullable().optional(),
    postcode: z.string().trim().max(16).nullable().optional(),
    phone: z.string().trim().max(32).nullable().optional(),
    lat: z.number().min(-90).max(90).nullable().optional(),
    lng: z.number().min(-180).max(180).nullable().optional(),
    hours: z.record(z.string(), z.unknown()).nullable().optional(),
    networkCidrs: z
      .array(z.string().trim().regex(CIDR_PATTERN, 'Not an IPv4 range'))
      .max(50)
      .optional(),
    sharedPcs: z.boolean().optional(),
    status: SiteStatusSchema.optional(),
    openedOn: IsoDate.nullable().optional(),
    closedOn: IsoDate.nullable().optional(),
    /** When closing a site that still has holders: end their seats on `closedOn`. */
    endHolders: z.boolean().optional(),
  })
  .refine((s) => s.status !== 'closed' || (s.closedOn !== undefined && s.closedOn !== null), {
    message: 'A closing date is needed to close a site',
    path: ['closedOn'],
  });
export type SitePatch = z.infer<typeof SitePatchSchema>;

export const SiteCreateSchema = z.object({
  code: Code,
  name: Name,
  kind: SiteKindSchema,
  format: SiteFormatSchema.default('trade'),
  regionCode: Code,
  divisionCode: Code,
  status: SiteStatusSchema.default('open'),
  openedOn: IsoDate.nullable().optional(),
  address: z.string().trim().max(255).nullable().optional(),
  postcode: z.string().trim().max(16).nullable().optional(),
  phone: z.string().trim().max(32).nullable().optional(),
});
export type SiteCreate = z.infer<typeof SiteCreateSchema>;

/** 409 when closing a site whose seats still have holders. */
export const SiteHasHoldersErrorSchema = z.object({
  error: z.literal('site_has_holders'),
  message: z.string(),
  holders: z.array(z.object({ personId: z.uuid(), name: z.string(), positionCode: z.string() })),
});
export type SiteHasHoldersError = z.infer<typeof SiteHasHoldersErrorSchema>;

export const PositionScopeInputSchema = z.object({
  kind: z.enum(['site', 'region', 'division']),
  code: Code,
});

export const PositionCreateSchema = z.object({
  code: Code.pipe(z.string().max(64)),
  title: Name,
  kind: PositionKindSchema,
  scope: PositionScopeInputSchema,
  grantsRoleCode: z.string().trim().max(64).nullable().optional(),
  reportsToCode: Code.nullable().optional(),
  roleMailbox: z.email().nullable().optional(),
});
export type PositionCreate = z.infer<typeof PositionCreateSchema>;

export const PositionPatchSchema = z.object({
  title: Name.optional(),
  grantsRoleCode: z.string().trim().max(64).nullable().optional(),
  reportsToCode: Code.nullable().optional(),
  roleMailbox: z.email().nullable().optional(),
  status: z.enum(['active', 'closed']).optional(),
  /** When closing a seat with a holder: end their assignment today. */
  endHolder: z.boolean().optional(),
});
export type PositionPatch = z.infer<typeof PositionPatchSchema>;

export const PositionCreatedSchema = z.object({ id: z.uuid(), code: z.string() });
export const PositionPatchedSchema = z.object({
  code: z.string(),
  sessionsRevoked: z.number().int().nonnegative(),
});

export const OrgUnitSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  status: OrgStatusSchema,
});
export type OrgUnit = z.infer<typeof OrgUnitSchema>;

// Queues ----------------------------------------------------------------------

export const QueueResolveRequestSchema = z.object({
  resolution: z.string().trim().min(1).max(500),
});
export type QueueResolveRequest = z.infer<typeof QueueResolveRequestSchema>;

/** Generic error body for 4xx answers from the admin routes. */
export const AdminErrorSchema = z.object({ error: z.string(), message: z.string() });
